import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const listenerPatternsCode = `// 1. Modern addEventListener - The Standard
const btn = document.querySelector('.action-btn');

function handleInteraction(event) {
    console.log("Interaction detected:", event.type);
}

// Support for multiple independent listeners
btn.addEventListener('click', handleInteraction);
btn.addEventListener('click', () => console.log("Analytics ping sent."));

// 2. Removal Logic - Requires a function reference
btn.removeEventListener('click', handleInteraction);

// 3. One-time Event Listeners
btn.addEventListener('click', () => {
    console.log("This executes exactly once.");
}, { once: true });`;

const eventObjectCode = `// The Event Object: An architectural snapshot of the interaction

document.addEventListener('mousemove', (e) => {
    // 1. Target vs CurrentTarget
    // .target is the deepest element clicked
    // .currentTarget is the element where the listener lives
    
    // 2. Coordinate Systems
    const { clientX, clientY } = e; // Relative to the Viewport
    const { pageX, pageY } = e;     // Relative to the Document (includes scroll)
    
    // 3. Modifier Detection
    if (e.ctrlKey || e.metaKey) {
        console.log("Power-user interaction detected.");
    }
});`;

const interactionControlCode = `// Controlling Browser Behavior

const form = document.querySelector('#signup-form');

form.addEventListener('submit', (e) => {
    // Prevent the default browser reload
    e.preventDefault();
    
    // Validate and send via fetch API
    const data = new FormData(e.target);
    processSignup(data);
});

// Passive Listeners for Scroll Performance
// Tells the engine we will NOT call preventDefault(), 
// allowing the compositor thread to scroll immediately.
window.addEventListener('scroll', handleScroll, { passive: true });`;

const customEventCode = `// Architectural Patterns: Custom Events

// Defining a custom protocol for component communication
const authStateChanged = new CustomEvent('auth:state-change', {
    detail: { 
        isLoggedIn: true, 
        user: 'rohit_dev' 
    },
    bubbles: true,
    cancelable: true
});

// Dispatching from a lower-level module
document.dispatchEvent(authStateChanged);

// Listening at the application root
document.addEventListener('auth:state-change', (e) => {
    updateUI(e.detail.user);
});`;

const JSEvents = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Events & Handling</div>
                <h1>The Event System Architecture</h1>
                <p className="hero-lead">
                    Master the high-performance event loop of the browser. Understand
                    the architectural bridge between user hardware and JavaScript logic,
                    learn to manage listener lifecycles, and optimize interaction
                    speed with advanced execution options.
                </p>
            </div>

            <div className="content-section">
                <h2>The Event Interface: Bridging Hardware to Logic</h2>
                <p>
                    JavaScript events are the fundamental asynchronous signals that the browser's
                    rendering engine emits whenever a meaningful state change occurs. Whether it is
                    a hardware interrupt from a mouse click, a network notification from a completed
                    request, or a timer firing, the event system is the mechanism that translates
                    these external signals into executable JavaScript tasks. Architecturally,
                    this system is built on the Observer pattern: the browser maintains a registry
                    of "listeners" for various event types and executes the associated callback
                    functions when the corresponding signal is detected. This allows your application
                    to remain responsive, as the main thread is not "waiting" for a click;
                    instead, it is free to perform other computations until the browser's
                    Event Loop picks up the signal and schedules your handler for execution.
                </p>
                <p>
                    A critical architectural evolution in modern browsers is the `addEventListener`
                    API, which replaced the legacy "one-handler-per-element" properties like
                    `onclick`. The legacy approach was fragile because any script could
                    inadvertently overwrite a previously established handler, leading to silent
                    failures in external libraries or plugins. Modern listeners are additive and
                    supports multiple independent callbacks for the same event type on a single
                    node. This enables a modular architecture where an "Analytics" module, a
                    "UI" module, and a "Validation" module can all listen to the same
                    button click without knowing about or interfering with each other's code.
                    This decoupling is essential for scaling complex front-ends where
                    multiple teams are contributing to the same interface.
                </p>

                <CodeBlock code={listenerPatternsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Event Object: A Runtime Snapshot</h2>
                <p>
                    Every time an event handler is invoked, the browser carefully constructs an
                    **Event Object** and passes it as the primary argument to your function.
                    This object is a comprehensive architectural snapshot that captures the state of
                    the environment at the exact microsecond the interaction occurred. It includes
                    generic metadata like the type of event and the timestamp, but also specialized
                    data depending on the category. For instance, `KeyboardEvents` carry the `key`
                    and `code` properties representing physical hardware keys, while `MouseEvents`
                    provide precise coordinate telemetry relative to the viewport, the page,
                    and even the user's entire screen. Understanding how to extract this
                    data is key to building predictive search bars, drag-and-drop systems,
                    and immersive interactive visualizations.
                </p>
                <p>
                    One of the most important architectural distinctions within the Event Object is
                    the difference between `target` and `currentTarget`. The `target` property
                    points to the "initiator"the deepest element in the DOM tree that the
                    user actually interacted with (e.g., the specific icon inside a button).
                    The `currentTarget`, however, points to the element that is actually
                    invoking the listener currently being executed. Confusing these two is a
                    major source of bugs in complex UI components. Senior engineers leverage
                    this distinction to implement "Event Delegation," where a single
                    listener on a parent container can precisely manage the behavior of
                    thousands of child elements by inspecting the `target` property of
                    daily events.
                </p>

                <CodeBlock code={eventObjectCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Interaction Control: Defaults and Performance</h2>
                <p>
                    Browsers come with an extensive set of "Default Behaviors" built into their
                    DNAclicking a link navigates to a new URL, submitting a form reloads the
                    entire page, and right-clicking invokes a context menu. In the era of Single
                    Page Applications (SPAs), we often need to suppress these native actions
                    to implement our own custom logic (like client-side routing). The
                    `preventDefault()` method is the architectural switch that tells the
                    browser's rendering engine to abort its standard operation and yield
                    complete control to your JavaScript handler. This is essential for
                    building fluid, application-like experiences that don't suffer from
                    the disruptive jarring of full-page reloads every time a user
                    interacts with a form.
                </p>
                <p>
                    However, yielding this control comes with a performance trade-off. For high-frequency
                    events like `scroll` or `touchstart`, the browser's compositor thread normally
                    wants to update the UI immediately for maximum smoothness. If it has to wait
                    for your JavaScript to decide whether to call `preventDefault()`, the scrolling
                    experience will feel "janky" or delayed. To solve this, browsers introduced
                    **Passive Event Listeners**. By setting the <code>{'{ passive: true }'}</code> option, you
                    guarantee to the engine that you will *never* call `preventDefault()`. This
                    allows the browser to process user scrolling instantly on a separate high-priority
                    thread without waiting for the main JavaScript thread to finish its work.
                    Implementing passive listeners on global scroll and touch events is
                    a hallmark of production-grade performance engineering.
                </p>

                <CodeBlock code={interactionControlCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Advanced Communication: Custom Event Protocols</h2>
                <p>
                    While the browser provides hundreds of built-in events, the most robust application
                    architectures often define their own **Custom Event Protocols**. By using the
                    `CustomEvent` constructor, you can create and dispatch events that represent
                    domain-specific actions, such as `user-authenticated`, `theme-switched`, or
                    `cart-updated`. These events can carry a `detail` objecta payload of
                    structured data that travels through the DOM tree just like a native click.
                    This pattern allows for elegant, decoupled communication between
                    components that are located far apart in the document hierarchy. A
                    bottom-level "Buy" button can dispatch a `cart:add` event that
                    bubbles up to the header's "Cart" icon, keeping the two modules
                    completely independent of each other's internal logic.
                </p>
                <p>
                    Custom events also support the standard propagation features, including
                    bubbling and cancellation. This means you can build "Gatekeeper" components
                    that listen for specific actions and prevent them from completing if
                    certain conditions aren't met. For example, a validation layer can
                    intercept a `step:next` event and call `stopPropagation()` if the
                    current form is invalid. This declarative, event-driven approach is
                    often cleaner and more maintainable than passing callback functions deep
                    into a nested component tree. By mastering the dispatch and
                    interception of custom signals, you transform the DOM from a simple
                    visual structure into a high-speed messaging bus for your
                    application's internal state.
                </p>

                <CodeBlock code={customEventCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Stability:** Always use `addEventListener` for additive, modular handlers.</li>
                    <li>**Lifespan:** Use named function references if you need to remove listeners later.</li>
                    <li>**Surgicality:** Distinguish between `target` (origin) and `currentTarget` (owner).</li>
                    <li>**Resilience:** Call `preventDefault()` to manage the browser's native behaviors.</li>
                    <li>**Smoothness:** Use <code>{'{ passive: true }'}</code> for non-blocking scroll and touch logic.</li>
                    <li>**Messaging:** Leverage `CustomEvent` for clean, decoupled component communication.</li>
                    <li>**Cleanliness:** Remove listeners during cleanup to prevent detached DOM memory leaks.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSEvents;

